/**
 * @fileoverview Composable for fetching and caching guilds (business categories).
 *
 * Provides a reactive list of available guilds with caching per culture.
 */

import { computed, ref, watch } from "vue";
import { useLanguages } from "@/core/composables/useLanguages";
import { getGuilds } from "../api";
import type { IGuildOption } from "../types";
import type { ComputedRef, Ref } from "vue";

// Module-level cache for guilds - keyed by culture name
const guildsCacheByculture = ref<Record<string, IGuildOption[]>>({});
const loadedCultures = ref<Set<string>>(new Set());
const isLoadingState = ref(false);
const loadError = ref<string | null>(null);

export interface IUseGuildsReturn {
  guilds: ComputedRef<IGuildOption[]>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  loadGuilds: () => Promise<void>;
  getGuildByValue: (value: string) => IGuildOption | undefined;
}

/**
 * Composable for fetching and managing guild options.
 * Automatically loads guilds in the current language.
 */
export function useGuilds(): IUseGuildsReturn {
  const { currentLanguage } = useLanguages();

  const currentCulture = computed(() => {
    return currentLanguage.value?.cultureName ?? "en-US";
  });

  const guilds = computed(() => {
    return guildsCacheByculture.value[currentCulture.value] ?? [];
  });

  /**
   * Load guilds from the API if not already cached for current culture.
   */
  async function loadGuilds(): Promise<void> {
    const culture = currentCulture.value;

    if (loadedCultures.value.has(culture) || isLoadingState.value) {
      return;
    }

    isLoadingState.value = true;
    loadError.value = null;

    try {
      const result = await getGuilds(culture);
      guildsCacheByculture.value[culture] = result;
      loadedCultures.value.add(culture);
    } catch (err) {
      loadError.value = err instanceof Error ? err.message : "Failed to load guilds";
    } finally {
      isLoadingState.value = false;
    }
  }

  // Watch for language changes and reload guilds if needed
  watch(currentCulture, () => {
    if (!loadedCultures.value.has(currentCulture.value)) {
      void loadGuilds();
    }
  });

  /**
   * Get a guild by its value.
   */
  function getGuildByValue(value: string): IGuildOption | undefined {
    return guilds.value.find((g) => g.value === value);
  }

  return {
    guilds,
    isLoading: isLoadingState,
    error: loadError,
    loadGuilds,
    getGuildByValue,
  };
}
